import React, { createContext, useContext, useState, useEffect } from "react";
import en from "../locales/en.json";
import enAU from "../locales/en-AU.json";
import enNZ from "../locales/en-NZ.json";
import ar from "../locales/ar.json";
import zhCN from "../locales/zh-CN.json";

const translations = {
  en: en,
  "en-au": enAU,
  "en-nz": enNZ,
  ar: ar,
  "zh-cn": zhCN,
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem("sk_woollens_lang") || "en";
  });

  useEffect(() => {
    localStorage.setItem("sk_woollens_lang", language);
    
    // Set direction and document language attributes
    if (language === "ar") {
      document.documentElement.setAttribute("dir", "rtl");
      document.documentElement.setAttribute("lang", "ar");
      document.body.classList.add("rtl-mode");
    } else {
      document.documentElement.setAttribute("dir", "ltr");
      document.documentElement.setAttribute("lang", language);
      document.body.classList.remove("rtl-mode");
    }
  }, [language]);

  const changeLanguage = (newLang) => {
    const normalized = newLang.toLowerCase();
    if (translations[normalized]) {
      setLanguage(normalized);
    }
  };

  // Safe translation resolver with dot notation and fallback
  const t = (path) => {
    const currentDict = translations[language] || translations["en"];
    const keys = path.split(".");
    
    let result = currentDict;
    for (const key of keys) {
      if (result && result[key] !== undefined) {
        result = result[key];
      } else {
        // Fallback to English
        let fallback = translations["en"];
        for (const fbKey of keys) {
          if (fallback && fallback[fbKey] !== undefined) {
            fallback = fallback[fbKey];
          } else {
            return path;
          }
        }
        return fallback;
      }
    }
    return result;
  };

  // Dynamic path helper for localized navigation
  const lPath = (path) => {
    if (path === "/") return `/${language}`;
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `/${language}${cleanPath}`;
  };

  const isRTL = language === "ar";

  return (
    <LanguageContext.Provider
      value={{
        language,
        changeLanguage,
        t,
        lPath,
        isRTL,
        availableLanguages: [
          { code: "en", name: "English", label: "English" },
          { code: "en-au", name: "Australia", label: "Australia" },
          { code: "en-nz", name: "New Zealand", label: "New Zealand" },
          { code: "ar", name: "العربية", label: "العربية" },
          { code: "zh-cn", name: "中文", label: "中文" },
        ],
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
export default LanguageContext;
